import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { AppView, UserProfile, UserTaskState, Article } from '../types';
import { ArrowRight, CheckCircle, DollarSign, Users, RefreshCw, Settings, GraduationCap, Building2, MapPin, Sparkles, BookOpen, ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import { trackEvent } from '../utils/analytics';
import { appStorage } from '../utils/storage';
import { safeJsonParse, validateTaskStates } from '../utils/validation';
import { useData } from '../App';

const ARTICLES: Article[] = [
  {
    id: 'oci-aadhaar-guide',
    title: 'Demystifying OCI Passport Updates & Aadhaar Rules for Your Family',
    category: 'Documents & Visas',
    categoryColor: 'indigo',
    excerpt: 'The 90-day APIS deadline, the Age-20 reissue rule, and how long OCI children must wait before applying for Aadhaar.',
    readTimeMinutes: 8,
    publishedDate: '2026-01-15',
    heroImage: '/images/nri-guide-hero.png',
    appView: AppView.NRI_GUIDE,
    comingSoon: false,
  },
  {
    id: 'rnor-tax-window',
    title: 'The RNOR Tax Window: Your One-Time Opportunity to Sell US Stocks Tax-Free',
    category: 'Finance & Tax',
    categoryColor: 'emerald',
    excerpt: 'Most returning NRIs leave lakhs on the table by not acting in the 2-year RNOR window. Here is the exact playbook.',
    readTimeMinutes: 10,
    publishedDate: '2026-02-01',
    heroImage: null,
    heroVisual: 'rnor',
    appView: AppView.RNOR_GUIDE,
    comingSoon: false,
  },
  {
    id: 'school-admission-timeline',
    title: 'The IB/IGCSE School Admission Guide: Boards, Calendars & Mid-Year Realities',
    category: 'Schools & Education',
    categoryColor: 'amber',
    excerpt: 'Pune schools begin admissions as early as November for the following June. Miss the window and your child loses a year.',
    readTimeMinutes: 7,
    publishedDate: '2026-02-15',
    heroImage: null,
    heroVisual: 'school',
    appView: AppView.SCHOOL_GUIDE,
    comingSoon: false,
  },
  {
    id: 'pune-rental-guide',
    title: 'Renting in Pune as a Returning NRI: The Leave & Licence Agreement Explained',
    category: 'Housing',
    categoryColor: 'rose',
    excerpt: "Maharashtra's Leave & Licence model is not a standard tenancy lease. What every returnee must know before signing.",
    readTimeMinutes: 6,
    publishedDate: '',
    heroImage: null,
    heroVisual: 'rental',
    appView: null,
    comingSoon: true,
  },
];

const BADGE_CLASSES: Record<string, { bg: string; text: string; icon: string; gradient: string }> = {
  indigo:  { bg: 'bg-indigo-100',  text: 'text-indigo-700',  icon: 'text-indigo-300',  gradient: 'from-indigo-50 to-indigo-100'  },
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: 'text-emerald-300', gradient: 'from-emerald-50 to-emerald-100' },
  amber:   { bg: 'bg-amber-100',   text: 'text-amber-700',   icon: 'text-amber-300',   gradient: 'from-amber-50 to-amber-100'   },
  rose:    { bg: 'bg-rose-100',    text: 'text-rose-700',    icon: 'text-rose-300',    gradient: 'from-rose-50 to-rose-100'     },
};

interface ArticleCardProps {
  article: Article;
  onNavigate: () => void;
}

const ArticleCard: React.FC<ArticleCardProps> = ({ article, onNavigate }) => {
  const badge = BADGE_CLASSES[article.categoryColor] ?? BADGE_CLASSES.indigo;

  return (
    <div
      className={`flex-shrink-0 snap-start w-[calc(100vw-3rem)] sm:w-80 bg-white rounded-2xl border border-slate-100 overflow-hidden transition-all duration-300 ${
        article.comingSoon
          ? 'opacity-80 cursor-default'
          : 'hover:shadow-md hover:-translate-y-1 cursor-pointer group'
      }`}
      onClick={!article.comingSoon ? onNavigate : undefined}
      role={!article.comingSoon ? 'button' : undefined}
      tabIndex={!article.comingSoon ? 0 : undefined}
      onKeyDown={!article.comingSoon ? (e) => { if (e.key === 'Enter') onNavigate(); } : undefined}
    >
      {/* Image area */}
      <div className="relative h-40 bg-slate-100 overflow-hidden">
        {article.heroImage ? (
          <img
            src={article.heroImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : article.heroVisual === 'rnor' ? (
          <div className="w-full h-full bg-gradient-to-br from-slate-800 to-emerald-900 flex items-center justify-center relative overflow-hidden">
            <div className="absolute inset-0 flex items-end justify-around px-4 pb-0 opacity-20">
              {[35,50,42,65,55,72,60].map((h, i) => (
                <div key={i} className="bg-emerald-400 rounded-t-sm w-5" style={{ height: `${h}%` }} />
              ))}
            </div>
            <div className="relative z-10 flex items-center gap-4">
              <div className="text-center">
                <div className="text-3xl font-black text-white/90">$</div>
                <div className="text-[10px] text-emerald-300 font-bold uppercase tracking-wider">US Stocks</div>
              </div>
              <ArrowRight size={18} className="text-emerald-400" />
              <div className="text-center">
                <div className="text-3xl font-black text-white/90">₹</div>
                <div className="text-[10px] text-emerald-300 font-bold uppercase tracking-wider">Tax-Free</div>
              </div>
            </div>
          </div>
        ) : article.heroVisual === 'school' ? (
          <div className="w-full h-full bg-gradient-to-br from-slate-800 to-amber-900 flex items-center justify-center relative overflow-hidden">
            <div className="relative z-10 flex flex-col items-center gap-3 w-full px-4">
              <div className="flex gap-2 w-full justify-center">
                {[{ l: 'IB', c: 'bg-amber-500' }, { l: 'IGCSE', c: 'bg-orange-500' }, { l: 'CBSE', c: 'bg-yellow-600' }].map(b => (
                  <div key={b.l} className={`${b.c} rounded-lg px-3 py-2 text-center flex-1`}>
                    <div className="text-white font-black text-sm">{b.l}</div>
                  </div>
                ))}
              </div>
              <div className="text-[10px] text-amber-300 font-semibold uppercase tracking-widest">Choose your board</div>
            </div>
          </div>
        ) : article.heroVisual === 'rental' ? (
          <div className="w-full h-full bg-gradient-to-br from-slate-800 to-rose-900 flex items-center justify-center">
            <div className="bg-white/10 border border-white/20 rounded-xl px-5 py-3 text-center backdrop-blur-sm">
              <div className="text-white font-black text-base">Leave & Licence</div>
              <div className="text-rose-300 text-[10px] font-bold uppercase tracking-widest mt-0.5">Maharashtra Rental Law</div>
            </div>
          </div>
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${badge.gradient} flex items-center justify-center`}>
            <BookOpen size={40} className={badge.icon} />
          </div>
        )}
        {article.comingSoon && (
          <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-slate-800/80 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
            <Lock size={10} />
            Coming Soon
          </div>
        )}
      </div>

      {/* Card body */}
      <div className="p-5 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${badge.bg} ${badge.text}`}>
            {article.category}
          </span>
          <span className="text-[11px] text-slate-400 font-medium">{article.readTimeMinutes} min read</span>
        </div>

        <h3 className={`font-bold text-slate-800 leading-snug text-sm line-clamp-2 ${!article.comingSoon ? 'group-hover:text-indigo-700 transition-colors' : ''}`}>
          {article.title}
        </h3>

        <p className="text-slate-500 text-xs leading-relaxed line-clamp-2">{article.excerpt}</p>

        <div className="flex items-center justify-between pt-1 mt-auto">
          {article.comingSoon ? (
            <span className="text-slate-400 text-xs font-medium italic">Launching soon — stay tuned</span>
          ) : (
            <div className="flex items-center gap-1.5 text-indigo-600 font-bold text-xs group-hover:gap-2.5 transition-all duration-200">
              Read Guide <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
            </div>
          )}
          {!article.comingSoon && article.publishedDate && (
            <span className="text-slate-300 text-[10px]">
              {new Date(article.publishedDate).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

interface DashboardProps {
  userProfile: UserProfile;
  setView: (view: AppView) => void;
  onEditProfile: () => void;
  onResetProfile: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ userProfile, setView, onEditProfile, onResetProfile }) => {
  const { schools, neighborhoods, checklist } = useData();

  // State for checklist progress - enables real-time updates
  const [userTasks, setUserTasks] = useState<UserTaskState[]>([]);
  const [lastUpdate, setLastUpdate] = useState(Date.now());

  // Carousel state
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  const scrollCarousel = (dir: 'prev' | 'next') => {
    carouselRef.current?.scrollBy({ left: dir === 'next' ? 320 : -320, behavior: 'smooth' });
  };

  const onCarouselScroll = () => {
    if (!carouselRef.current) return;
    const cardW = carouselRef.current.scrollWidth / ARTICLES.length;
    setActiveIndex(Math.round(carouselRef.current.scrollLeft / cardW));
  };

  // Load tasks from localStorage with validation
  const loadTasks = useCallback(() => {
    const savedTasks = appStorage.getItem('puneRelocationProgress_v2');
    const parsed = safeJsonParse<unknown[]>(savedTasks);
    setUserTasks(validateTaskStates(parsed));
  }, []);

  // Initial load and set up storage event listener for cross-tab updates
  useEffect(() => {
    loadTasks();

    // Listen for storage changes (works for cross-tab updates)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'puneRelocationProgress_v2') {
        loadTasks();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also poll for changes every 2 seconds (for same-tab updates)
    const pollInterval = setInterval(() => {
      loadTasks();
      setLastUpdate(Date.now());
    }, 2000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(pollInterval);
    };
  }, [loadTasks]);

  // Refresh stats when component becomes visible (e.g., navigating back to dashboard)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadTasks();
        setLastUpdate(Date.now());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadTasks]);

  // --- STATS CALCULATION ---
  const stats = useMemo(() => {
    if (!userProfile) return null;

    // 1. Matching Schools
    // Include all curriculum types for school count - all boards are valid options
    // NRI families often choose IB/IGCSE for international recognition, but State Board is also popular
    const targetCurricula = ['IB', 'IGCSE', 'CBSE', 'State Board'];

    // Filter by location if specified
    const recommendedZones: Record<string, string[]> = {
      'hinjewadi': ['hinjewadi', 'baner', 'balewadi', 'wakad', 'pimple_saudagar', 'aundh', 'pashan', 'sus'],
      'kharadi': ['kharadi', 'viman_nagar', 'amanora', 'hadapsar', 'kalyani_nagar', 'wadgaon_sheri', 'mundhwa'],
      'magarpatta': ['amanora', 'hadapsar', 'kharadi', 'magarpatta', 'fatima_nagar', 'wanowrie', 'kondhwa']
    };
    const targetLocs = recommendedZones[userProfile.workLocation] || [];

    // Count matching schools (with defensive null checks)
    const matchedSchools = schools.filter(s => {
      // Check curriculum match - ensure curriculum is an array
      const curriculum = Array.isArray(s.curriculum) ? s.curriculum : [];
      const currMatch = curriculum.some(c => targetCurricula.includes(c));

      // For location: if user has a specific work location, filter by nearby areas
      // If user is WFH/unsure, show all schools matching curriculum
      const localities = Array.isArray(s.nearbyLocalities) ? s.nearbyLocalities : [];
      const locMatch = targetLocs.length > 0
        ? localities.some(l => targetLocs.includes(l.toLowerCase().replace(/\s+/g, '_')))
        : true;

      return currMatch && locMatch;
    }).length;

    // 2. Matching Neighborhoods
    const matchedNeighborhoods = neighborhoods.filter(n => {
      // If user has specific work location, prioritize nearby neighborhoods
      if (targetLocs.length > 0) {
        // Check if neighborhood ID matches or is nearby recommended zones
        const isNearby = targetLocs.some(tl =>
          n.id.toLowerCase().includes(tl) ||
          tl.includes(n.id.toLowerCase()) ||
          n.nearbySchools?.some(ns => ns.toLowerCase().includes(tl))
        );
        return isNearby;
      }
      // If WFH or unsure, show all neighborhoods
      return true;
    }).length;

    // 3. Checklist Progress - now using state instead of direct localStorage read
    let totalTasks = 0;
    let completedTasks = 0;

    const country = userProfile.sourceCountry === 'UK' ? 'UK'
                  : userProfile.sourceCountry === 'Germany' ? 'Germany'
                  : 'US';

    checklist.phases.forEach(phase => {
      phase.categories.forEach(cat => {
        cat.tasks.forEach(task => {
          // Task applies to user if it's for their country or is universal (empty array)
          if (task.sourceCountry.includes(country) || task.sourceCountry.length === 0) {
            totalTasks++;
            const userTask = userTasks.find(t => t.id === task.id);
            if (userTask?.completed) {
              completedTasks++;
            }
          }
        });
      });
    });

    const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    // 4. Count FAQs (dynamic)
    const faqCount = 25; // This could also be made dynamic from useData()

    return {
      matchedSchools,
      matchedNeighborhoods,
      completedTasks,
      totalTasks,
      progress,
      faqCount
    };
  }, [userProfile, schools, neighborhoods, checklist, userTasks, lastUpdate]);

  // Helper for formatting work location display
  const getWorkLocationDisplay = () => {
    switch (userProfile.workLocation) {
      case 'wfh': return 'Work From Home';
      case 'unsure': return 'Exploring Locations';
      case 'hinjewadi': return 'Office: Hinjewadi IT Park';
      case 'kharadi': return 'Office: Kharadi/EON IT Park';
      case 'magarpatta': return 'Office: Magarpatta City';
      default: return `Office: ${userProfile.workLocation.charAt(0).toUpperCase() + userProfile.workLocation.slice(1)}`;
    }
  };

  // Helper for formatting family type
  const getFamilyTypeDisplay = () => {
    switch (userProfile.familyType) {
      case 'family_with_kids': return 'Family with Kids';
      case 'couple_no_kids': return 'Couple';
      case 'single': return 'Single';
      default: return 'Single/Couple';
    }
  };

  // Helper for formatting budget
  const getBudgetDisplay = () => {
    const budget = userProfile.budget;
    if (budget.includes('rent')) {
      const amount = budget.replace('rent_', '').replace('_', '-');
      return `Rent ₹${amount}/mo`;
    }
    if (budget.includes('buy')) {
      const amount = budget.replace('buy_', '').replace('_', '-').replace('cr', ' Cr').replace('plus', '+');
      return `Buy ₹${amount}`;
    }
    return budget.replace(/_/g, ' ');
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8 pb-24 md:pb-6 animate-in fade-in duration-500">
      {/* HEADER */}
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="space-y-1">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800">
            {userProfile.isPersonalized ? 'Welcome Home 🇮🇳' : 'NRI Relocation Guide 🇮🇳'}
          </h1>
          <p className="text-slate-600">
            {userProfile.isPersonalized
              ? <>Your personalized guide for moving from <strong className="text-indigo-600">{userProfile.sourceCountry}</strong> to <strong>India</strong>.</>
              : <>Your complete guide to relocating to <strong>Pune, India</strong>. Explore neighborhoods, schools, checklists & more.</>
            }
          </p>
          <div className="inline-flex items-center gap-2 mt-2 px-3 py-1 bg-indigo-50 rounded-full text-indigo-700 text-xs font-medium">
            <MapPin size={12} />
            <span>Currently showing: <strong>Pune</strong></span>
            <span className="text-indigo-400">|</span>
            <span className="text-indigo-500">More cities coming soon</span>
          </div>
        </div>
        <div className="flex gap-2">
          <button
             onClick={onEditProfile}
             className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition"
          >
             {userProfile.isPersonalized ? <><Settings size={16} /> Preferences</> : <><Sparkles size={16} /> Personalize</>}
          </button>
          {userProfile.isPersonalized && (
            <button
               onClick={onResetProfile}
               className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-red-600 bg-red-50 border border-red-100 rounded-lg hover:bg-red-100 transition"
            >
               <RefreshCw size={16} /> Reset
            </button>
          )}
        </div>
      </header>

      {/* PERSONALIZATION PROMPT */}
      {!userProfile.isPersonalized && (
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-indigo-100 p-2 rounded-lg">
              <Sparkles size={20} className="text-indigo-600" />
            </div>
            <div>
              <p className="font-bold text-slate-800 text-sm">Get personalized recommendations</p>
              <p className="text-xs text-slate-500">Answer a few quick questions to customize schools, neighborhoods & checklists for your situation.</p>
            </div>
          </div>
          <button
            onClick={onEditProfile}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-5 py-2 rounded-lg shadow-sm transition whitespace-nowrap text-sm"
          >
            Personalize
          </button>
        </div>
      )}

      {/* PROFILE SUMMARY */}
      <div className="bg-indigo-900 rounded-2xl p-6 text-white shadow-xl relative overflow-hidden animate-in fade-in slide-in-from-top-4 duration-700">
         <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="space-y-2">
               <h2 className="text-xl font-bold text-indigo-100 uppercase tracking-wide text-xs">Current Plan</h2>
               <div className="flex flex-wrap gap-3">
                 <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-lg border border-white/10 backdrop-blur-sm">
                    <MapPin size={16} className="text-indigo-300" />
                    <span className="font-semibold text-sm">{getWorkLocationDisplay()}</span>
                 </div>
                 <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-lg border border-white/10 backdrop-blur-sm">
                    <Users size={16} className="text-indigo-300" />
                    <span className="font-semibold text-sm">{getFamilyTypeDisplay()}</span>
                 </div>
                 <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-lg border border-white/10 backdrop-blur-sm">
                    <DollarSign size={16} className="text-indigo-300" />
                    <span className="font-semibold text-sm">{getBudgetDisplay()}</span>
                 </div>
               </div>
            </div>

            <div className="text-center md:text-right">
               <div className="text-4xl font-bold text-white mb-1">{stats?.progress ?? 0}%</div>
               <div className="text-indigo-300 text-sm font-medium">Checklist Complete</div>
               {stats && stats.completedTasks > 0 && (
                 <div className="text-indigo-400 text-xs mt-1">
                   {stats.completedTasks} of {stats.totalTasks} tasks done
                 </div>
               )}
            </div>
         </div>

         {/* Decorative Circles */}
         <div className="absolute -top-10 -right-10 w-40 h-40 bg-indigo-600 rounded-full blur-3xl opacity-50"></div>
         <div className="absolute bottom-0 left-10 w-24 h-24 bg-purple-600 rounded-full blur-3xl opacity-40"></div>
      </div>

      {/* ZONE 1: PUNE GUIDE */}
      <div className="animate-in fade-in slide-in-from-bottom-4" style={{ animationDelay: '0ms' }}>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-bold uppercase tracking-widest text-amber-600">📍 Pune Guide</span>
          <div className="flex-1 h-px bg-amber-100"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Neighborhoods */}
          <div
            onClick={() => setView(AppView.NEIGHBORHOODS)}
            className="bg-white p-6 rounded-xl shadow-sm border border-amber-100/60 hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition">
                <Building2 size={24} />
              </div>
              <span className="text-2xl font-bold text-slate-800">{stats?.matchedNeighborhoods ?? 0}</span>
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-1">Neighborhoods</h3>
            <p className="text-slate-500 text-xs mb-4">
              {userProfile.workLocation === 'unsure' || userProfile.workLocation === 'wfh'
                ? 'All areas to explore'
                : 'Near your workplace'}
            </p>
            <div className="flex items-center text-blue-600 font-bold text-sm">
              Explore <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>

          {/* Schools */}
          <div
            onClick={() => setView(AppView.SCHOOLS)}
            className="bg-white p-6 rounded-xl shadow-sm border border-amber-100/60 hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="h-12 w-12 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600 group-hover:bg-emerald-600 group-hover:text-white transition">
                <GraduationCap size={24} />
              </div>
              <span className="text-2xl font-bold text-slate-800">
                {userProfile.familyType === 'family_with_kids' ? (stats?.matchedSchools ?? 0) : '-'}
              </span>
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-1">Recommended Schools</h3>
            <p className="text-slate-500 text-xs mb-4">
              {userProfile.familyType === 'family_with_kids'
                ? `${userProfile.sourceCountry === 'US' ? 'IB/IGCSE' : userProfile.sourceCountry === 'UK' ? 'IGCSE/IB' : 'All curricula'} schools`
                : 'Not needed for current profile'}
            </p>
            <div className="flex items-center text-emerald-600 font-bold text-sm">
              View Matches <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </div>
      </div>

      {/* ZONE 2: NRI BLUEPRINT */}
      <div className="animate-in fade-in slide-in-from-bottom-4" style={{ animationDelay: '150ms' }}>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-bold uppercase tracking-widest text-indigo-500">🇮🇳 NRI Blueprint</span>
          <div className="flex-1 h-px bg-indigo-100"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Checklist */}
          <div
            onClick={() => setView(AppView.CHECKLIST)}
            className="bg-white p-6 rounded-xl shadow-sm border border-indigo-100/60 hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center text-green-600 group-hover:bg-green-600 group-hover:text-white transition">
                <CheckCircle size={24} />
              </div>
              <span className="text-2xl font-bold text-slate-800">
                {stats ? (stats.totalTasks - stats.completedTasks) : 0}
              </span>
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-1">Tasks Remaining</h3>
            <p className="text-slate-500 text-xs mb-4">
              {stats && stats.completedTasks > 0
                ? `${stats.completedTasks} completed`
                : 'Start your checklist'}
            </p>
            <div className="flex items-center text-green-600 font-bold text-sm">
              Track Progress <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>

          {/* Community */}
          <div
            onClick={() => setView(AppView.COMMUNITY)}
            className="bg-white p-6 rounded-xl shadow-sm border border-indigo-100/60 hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600 group-hover:bg-orange-600 group-hover:text-white transition">
                <Users size={24} />
              </div>
              <span className="text-2xl font-bold text-slate-800">25+</span>
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-1">Community Insights</h3>
            <p className="text-slate-500 text-xs mb-4">Real advice from WhatsApp groups</p>
            <div className="flex items-center text-orange-600 font-bold text-sm">
              Read FAQs <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </div>
      </div>

      {/* ARTICLE CAROUSEL */}
      <section className="animate-in fade-in slide-in-from-bottom-4" style={{ animationDelay: '400ms' }}>
        {/* Section header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 bg-indigo-100 rounded-lg flex items-center justify-center">
              <BookOpen size={18} className="text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">NRI Relocation Guides</h2>
              <p className="text-xs text-slate-500">Expert-written deep dives for your move home</p>
            </div>
          </div>
          {/* Desktop arrow buttons */}
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => scrollCarousel('prev')}
              disabled={activeIndex === 0}
              className="h-8 w-8 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:border-indigo-400 hover:text-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed transition"
              aria-label="Previous article"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => scrollCarousel('next')}
              disabled={activeIndex === ARTICLES.length - 1}
              className="h-8 w-8 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:border-indigo-400 hover:text-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed transition"
              aria-label="Next article"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* Scrollable card track */}
        <div
          ref={carouselRef}
          onScroll={onCarouselScroll}
          className="flex gap-4 overflow-x-auto pb-2 scroll-smooth snap-x snap-mandatory [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {ARTICLES.map((article) => (
            <ArticleCard
              key={article.id}
              article={article}
              onNavigate={() => {
                if (article.appView) {
                  trackEvent('article_card_clicked', { articleId: article.id, category: article.category });
                  setView(article.appView);
                }
              }}
            />
          ))}
        </div>

        {/* Dot indicators (mobile only) */}
        <div className="flex justify-center gap-1.5 mt-3 md:hidden">
          {ARTICLES.map((_, i) => (
            <button
              key={i}
              onClick={() => {
                if (!carouselRef.current) return;
                const cardW = carouselRef.current.scrollWidth / ARTICLES.length;
                carouselRef.current.scrollTo({ left: cardW * i, behavior: 'smooth' });
                setActiveIndex(i);
              }}
              className={`rounded-full transition-all duration-300 ${
                i === activeIndex ? 'w-5 h-1.5 bg-indigo-600' : 'w-1.5 h-1.5 bg-slate-300'
              }`}
              aria-label={`Go to article ${i + 1}`}
            />
          ))}
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
